import React, { useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Input } from '@/components/ui/inputs/input';
import { resetPasswordAuthService } from '@/services/AuthServices';
import validationErrors from '@/lib/validationErrors';

const BRAND = { primary: '#156372', yellow: '#FFC107', ink: '#0F2F35' };

const safeLS = (k) => { try { return localStorage.getItem(k) || ''; } catch { return ''; } };

export default function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const email = useMemo(() => {
    const viaState = location?.state?.email || '';
    const viaQuery = (() => { try { return new URLSearchParams(location.search).get('email') || ''; } catch { return ''; } })();
    return (viaState || viaQuery || safeLS('fp_email') || '').trim();
  }, [location]);

  const token = useMemo(() => {
    const viaState = location?.state?.token || '';
    const viaQuery = (() => { try { return new URLSearchParams(location.search).get('token') || ''; } catch { return ''; } })();
    return (viaState || viaQuery || safeLS('fp_token') || '').trim();
  }, [location]);

  const { control, handleSubmit, formState: { errors }, watch } = useForm();

  const onSubmit = (data) => {
    if (!email || !token) return;
    if (data.password !== data.confirm) {
      validationErrors(null, 'Passwords do not match'); return;
    }
    setIsLoading(true);
    dispatch(resetPasswordAuthService({ email, token, password: data.password }))
      .then(() => {
   try {
     localStorage.removeItem('fp_token');
     localStorage.removeItem('fp_email');   // ← important
   } catch {}
   navigate('/sign-in', { replace: true });
      })
      .catch(({ message, errorData } = {}) => {
        validationErrors(errorData, message);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <section className="min-h-screen flex items-center" style={{ backgroundColor: BRAND.yellow }}>
      <div className="mx-auto max-w-7xl px-4 py-10 lg:py-16 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative z-10 lg:-mr-12">
              <div className="relative rounded-3xl shadow-2xl ring-1 ring-black/5 overflow-hidden" style={{ backgroundColor: BRAND.primary }}>
                <div className="absolute inset-y-0 left-0 w-1" style={{ backgroundColor: BRAND.yellow }} />
                <div className="p-6 md:p-10">
                  <h1 className="text-3xl md:text-4xl font-semibold text-center tracking-tight text-white mb-6">
                    Create New Password
                  </h1>

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <Controller
                      control={control}
                      name="password"
                      rules={{ required: true, minLength: 6, maxLength: 50 }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="password"
                          labelText="New Password"
                          placeholder="Enter a strong password"
                          errorData={{ minLength: '6', maxLength: '50' }}
                          errorType={errors?.password?.type}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="confirm"
                      rules={{ required: true, minLength: 6, maxLength: 50 }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="password"
                          labelText="Confirm Password"
                          placeholder="Re-enter the password"
                          errorData={{ minLength: '6', maxLength: '50' }}
                          errorType={errors?.confirm?.type}
                        />
                      )}
                    />

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-3 rounded-xl font-semibold shadow focus:outline-none focus:ring-2 hover:brightness-95 active:brightness-90 disabled:opacity-80 disabled:cursor-not-allowed"
                      style={{ backgroundColor: BRAND.yellow, color: BRAND.ink, boxShadow: '0 12px 30px rgba(0,0,0,.25)' }}
                    >
                      {isLoading ? 'Updating…' : 'Update Password'}
                    </button>

                    <div className="text-center pt-2">
                      <Link to="/sign-in" className="text-sm font-medium hover:underline" style={{ color: BRAND.yellow }}>
                        Back to login
                      </Link>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Right visual */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div
              className="relative h-[44vh] sm:h-[50vh] md:h-[58vh] lg:h-[78vh] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5"
              style={{ clipPath: 'polygon(7% 0%, 100% 0%, 100% 100%, 0% 100%)' }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
